import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useGameLoop } from '../Shared/hooks/useGameLoop';
import { useAudio } from '../Shared/hooks/useAudio';
import GameLayout from '../Shared/components/GameLayout/GameLayout';
import gameLayoutStyles from '../Shared/components/GameLayout/GameLayout.module.css';
import { GAME_THEME } from '../Shared/constants/GameTheme';
import { inchesToPixels, TARGET_REAL } from './sizeUtils';
import CalibrationStep from './CalibrationStep';
import Countdown from '../Shared/components/Countdown/Countdown';
import HistoryModal from '../Shared/components/HistoryModal/HistoryModal';
import HeatMapModal from '../Shared/components/HeatMapModal/HeatMapModal';
import { saveGameResult, getGameHistory, deleteGameResult } from '../Shared/utils/HistoryUtils';

import { BELT_LEVELS, BELT_COLORS, DRILLS, FULL_DRILL, ADDITIONAL_DRILLS, CLICK6_DRILLS, ALL_DRILLS, evaluateBelt, evaluateFullBelt } from './DrillConstants';
import PropTypes from 'prop-types';

import { useNavigate } from 'react-router-dom';
import * as styles from './SamuraiStyles';

// ─── Component ─────────────────────────────────────────────────────────────────
const SamuraiGame = ({ initialDrillIndex = 0 }) => {
    const canvasRef = useRef(null);
    const { beep, ping } = useAudio();
    const navigate = useNavigate();

    // ── Calibration state ──────────────────────────────────────────────────────
    const savedCal = (() => {
        try { return JSON.parse(localStorage.getItem('msp_cal')); } catch { return null; }
    })();
    const [screenWidthIn, setScreenWidthIn] = useState(savedCal?.screenWidthIn ?? 60);
    const [viewDistIn, setViewDistIn] = useState(savedCal?.viewDistIn ?? 72);
    const [retentionHolster, setRetentionHolster] = useState(savedCal?.retentionHolster ?? false);
    const [selectedDrillIndex, setSelectedDrillIndex] = useState(initialDrillIndex);

    // ── Game state ────────────────────────────────────────────────────────────
    const [gameState, setGameState] = useState(savedCal ? 'menu' : 'calibrating');
    const [results, setResults] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [showHeatMap, setShowHeatMap] = useState(false);
    const [showHowToPlay, setShowHowToPlay] = useState(false);
    const [isAdditionalExpanded, setIsAdditionalExpanded] = useState(initialDrillIndex >= 5);
    const [history, setHistory] = useState([]);

    useEffect(() => {
        setHistory(getGameHistory('modern-samurai'));
    }, []);

    const handleDeleteDataPoint = useCallback((point) => {
        if (!point || !point.id) return;
        deleteGameResult('modern-samurai', point.id);
        setHistory(getGameHistory('modern-samurai'));
    }, []);

    // ── Earned belt (persisted) ───────────────────────────────────────────────
    const BELT_RANK = { blue: 1, purple: 2, brown: 3, black: 4 };
    const [earnedBelt, setEarnedBelt] = useState(
        () => localStorage.getItem('msp_earned_belt') || null
    );
    const saveEarnedBelt = useCallback((belt) => {
        if (!belt || belt === 'miss') return;
        setEarnedBelt(prev => {
            const prevRank = BELT_RANK[prev] ?? 0;
            const newRank = BELT_RANK[belt] ?? 0;
            if (newRank > prevRank) {
                localStorage.setItem('msp_earned_belt', belt);
                return belt;
            }
            return prev;
        });
    }, []); // eslint-disable-next-line

    // ── Laser input latency offset ───────────────────────────────────────────
    // Read from TestLatency calibrator (‘calibratedLatency’ key, stored in ms).
    // This is the extra delay the laser clicker adds vs a direct mouse click.
    // We subtract it from each recorded hit time to get the "true" reaction time.
    const [inputLatencyMs] = useState(
        () => Math.max(0, Number(localStorage.getItem('calibratedLatency') || 0))
    );

    // ── Full-test state ───────────────────────────────────────────────────────
    // fullTestDrillIdxRef: which of the 4 drills (0-3) we're currently running
    // fullTestTimesRef: accumulated times for each completed drill
    const fullTestDrillIdxRef = useRef(0);
    const fullTestTimesRef = useRef([]); // number[]
    const [fullTestResults, setFullTestResults] = useState(null); // set on completion

    // activeDrillRef: the actual drill being executed right now
    // (in full-test mode this cycles through DRILLS[0..3])
    useEffect(() => {
        setGameState('menu');
    }, [selectedDrillIndex]);

    const activeDrillRef = useRef(DRILLS[selectedDrillIndex]);
    const drillRef = useRef(ALL_DRILLS[selectedDrillIndex]);

    useEffect(() => {
        drillRef.current = ALL_DRILLS[selectedDrillIndex];
        if (!drillRef.current.isFullTest) {
            activeDrillRef.current = drillRef.current;
        }
    }, [selectedDrillIndex]);

    const stateRef = useRef({
        running: false,
        phase: 'idle',
        startTime: 0,
        shotsHit: 0,
        totalShots: 0,
        targets: [],
        clickLocations: [],
        endTime: null,
        drill: null,
        theta: 0,
        omega: 0,
        lastLoopTime: null,
    });

    // ── Calibration ───────────────────────────────────────────────────────────
    const handleCalibrationApply = useCallback(({ screenWidthIn: sw, viewDistIn: vd }) => {
        setScreenWidthIn(sw);
        setViewDistIn(vd);
        try { localStorage.setItem('msp_cal', JSON.stringify({ screenWidthIn: sw, viewDistIn: vd, retentionHolster })); } catch { /* ignore */ }
        setGameState('menu');
    }, [retentionHolster]);

    // ── Retention holster: +0.2s to all par times ─────────────────────────────
    const RETENTION_PENALTY = 0.2;
    const effectivePar = useCallback((drill) => {
        if (!retentionHolster) return drill.parTimes;
        return Object.fromEntries(Object.entries(drill.parTimes).map(([k, v]) => [k, v + RETENTION_PENALTY]));
    }, [retentionHolster]);

    // ── Helper: Build Targets ─────────────────────────────────────────────────
    const buildTargets = useCallback((drill, canvas) => {
        const w = canvas.width;
        const h = canvas.height;

        function getPx(yardage, realIn) {
            return inchesToPixels(realIn, w, screenWidthIn, viewDistIn, yardage);
        }

        if (drill.isClick6) {
             const rPx = getPx(drill.yardage, 8/2); // 8 inch plate
             const targets = [];
             if (drill.click6Mode === 'grid') {
                 const spacingX = rPx * 2.5;
                 const count = 6;
                 const totalW = (count - 1) * spacingX;
                 const startX = w/2 - totalW/2;
                 for (let i = 0; i < count; i++) {
                     targets.push({ zone: 'plate', cx: startX + i * spacingX, cy: h/2, halfW: rPx, halfH: rPx, hit: false, hitX: null, hitY: null, hitTime: null });
                 }
             } else if (drill.click6Mode === 'w_pattern') {
                 const topY = h * 0.25;
                 const bottomY = h * 0.75;
                 const midY = h * 0.45;
                 const wPoints = [
                     { x: w * 0.15, y: topY },
                     { x: w * 0.29, y: bottomY },
                     { x: w * 0.43, y: midY },
                     { x: w * 0.57, y: midY },
                     { x: w * 0.71, y: bottomY },
                     { x: w * 0.85, y: topY },
                 ];
                 wPoints.forEach(p => targets.push({ zone: 'plate', cx: p.x, cy: p.y, halfW: rPx, halfH: rPx, hit: false, hitX: null, hitY: null, hitTime: null }));
             } else {
                 // random
                 for (let i = 0; i < drill.shots.length; i++) {
                     let valid = false;
                     let cx = 0, cy = 0;
                     let attempts = 0;
                     while(!valid && attempts < 100) {
                         cx = rPx + Math.random() * (w - 2*rPx);
                         cy = rPx + Math.random() * (h - 2*rPx);
                         valid = !targets.some(t => Math.hypot(t.cx - cx, t.cy - cy) < 2.5 * rPx);
                         attempts++;
                     }
                     targets.push({ zone: 'plate', cx, cy, halfW: rPx, halfH: rPx, hit: false, hitX: null, hitY: null, hitTime: null });
                 }
             }
             return targets;
        }

        if (drill.isTexasStar) {
             const rPx = getPx(drill.yardage, 8/2); // 8 inch plate
             const armPx = getPx(drill.yardage, 20); // 20 inch arm
             return drill.shots.map((shot, i) => {
                 const phi = (i * 2 * Math.PI) / 5;
                 return { zone: 'plate', phi, armPx, cx: w/2 + Math.cos(phi)*armPx, cy: h/2 + Math.sin(phi)*armPx, halfW: rPx, halfH: rPx, hit: false, hitX: null, hitY: null, hitTime: null, attached: true, dropY: 0 };
             });
        }

        return drill.shots.map((shot) => {
            const currentYardage = shot.yardage || drill.yardage;
            const rPx = getPx(currentYardage, 8/2);
            function px(realIn) {
                return getPx(currentYardage, realIn);
            }

            const bx = shot.screenX !== undefined ? w * shot.screenX : w / 2;
            const by = shot.screenY !== undefined ? h * shot.screenY : h * 0.62;

            const azW = px(TARGET_REAL.bodyZoneWidth);
            const azH = px(TARGET_REAL.bodyZoneHeight);
            const hdW = px(TARGET_REAL.headWidth);
            const hdH = px(TARGET_REAL.headHeight);
            const hx = bx;
            const hy = by - px(TARGET_REAL.headOffsetY);

            if (shot.zone === 'body') {
                return { zone: 'body', cx: bx, cy: by, halfW: azW / 2, halfH: azH / 2, hit: false, hitX: null, hitY: null, hitTime: null };
            } else if (shot.zone === 'plate') {
                return { zone: 'plate', cx: bx, cy: by, halfW: rPx, halfH: rPx, hit: false, hitX: null, hitY: null, hitTime: null };
            } else {
                return { zone: 'head', cx: hx, cy: hy, halfW: hdW / 2, halfH: hdH / 2, hit: false, hitX: null, hitY: null, hitTime: null };
            }
        });
    }, [screenWidthIn, viewDistIn]);

    // ── Start Drill ───────────────────────────────────────────────────────────
    const startDrill = useCallback(() => {
        const drill = activeDrillRef.current;
        const canvas = canvasRef.current;
        if (!canvas || !drill) return;

        const state = stateRef.current;
        state.drill = drill;
        state.targets = buildTargets(drill, canvas);
        state.totalShots = drill.shots.length;
        state.shotsHit = 0;
        state.endTime = null;
        state.phase = 'playing';
        state.running = true;
        state.firstClickTime = null;
        state.totalClicks = 0;
        state.clickLocations = [];
        state.startTime = performance.now();
        state.lastLoopTime = performance.now();
        state.theta = 0;
        state.omega = 0;

        beep(1000, 0.1);
        setGameState('playing');
    }, [buildTargets, beep]);

    // ── Begin standby ─────────────────────────────────────────────────────────
    const beginStandby = useCallback(() => {
        const drill = activeDrillRef.current;
        const canvas = canvasRef.current;
        if (canvas && drill) {
            stateRef.current.drill = drill;
            stateRef.current.targets = buildTargets(drill, canvas);
            stateRef.current.phase = 'waiting';
        }
        setGameState('standby');
    }, [buildTargets]);

    // ── Begin full test ───────────────────────────────────────────────────────
    const beginFullTest = useCallback(() => {
        fullTestDrillIdxRef.current = 0;
        fullTestTimesRef.current = [];
        setFullTestResults(null);
        activeDrillRef.current = DRILLS[0];

        const drill = DRILLS[0];
        const canvas = canvasRef.current;
        if (canvas && drill) {
            stateRef.current.drill = drill;
            stateRef.current.targets = buildTargets(drill, canvas);
            stateRef.current.phase = 'waiting';
        }

        setGameState('standby');
    }, [buildTargets]);

    // ── Game Loop ──────────────────────────────────────────────────────────────
    const loop = useCallback((now) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const w = canvas.width;
        const h = canvas.height;
        const state = stateRef.current;

        ctx.fillStyle = GAME_THEME.BACKGROUND_COLOR;
        ctx.fillRect(0, 0, w, h);

        if (state.phase === 'idle' || state.phase === 'done') return;

        if (state.phase === 'playing' || state.phase === 'waiting') {
            const elapsed = state.phase === 'playing' ? now - state.startTime : 0;
            const dt = (now - (state.lastLoopTime || now)) / 1000 || 0.016;
            state.lastLoopTime = now;

            if (state.drill?.isTexasStar && state.phase === 'playing') {
                const G = 800;
                const TARGET_MASS = 1;
                const HUB_MASS = 2;
                const BEARING_DRAG = 0.9998;

                let torque = 0;
                let I = HUB_MASS * state.targets[0].armPx * state.targets[0].armPx;

                for (const p of state.targets) {
                    if (p.attached) {
                        const a = state.theta + p.phi;
                        torque += TARGET_MASS * G * p.armPx * Math.cos(a);
                        I += TARGET_MASS * p.armPx * p.armPx;
                    }
                }

                const alpha = torque / I;
                state.omega += alpha * dt;
                state.omega *= Math.pow(BEARING_DRAG, dt / 0.01666);
                state.theta += state.omega * dt;

                state.targets.forEach(t => {
                    if (t.attached) {
                        const a = state.theta + t.phi;
                        t.cx = (w / 2) + Math.cos(a) * t.armPx;
                        t.cy = (h / 2) + Math.sin(a) * t.armPx;
                    } else {
                        t.dropY += 600 * dt;
                        const a = state.thetaDrop + t.phi; // keep X from moment of drop
                        t.cx = (w / 2) + Math.cos(a) * t.armPx;
                        t.cy = (h / 2) + Math.sin(a) * t.armPx + t.dropY;
                    }
                });
            }

            drawTargets(ctx, state.targets, w, h, state.phase === 'playing', state.phase, state);

            if (state.phase === 'playing') {
                ctx.fillStyle = '#fff';
                ctx.font = `bold ${Math.max(22, w * 0.03)}px Inter, sans-serif`;
                ctx.textAlign = 'center';
                ctx.fillText((elapsed / 1000).toFixed(3) + 's', w / 2, h * 0.08);

                ctx.fillStyle = 'rgba(255,255,255,0.5)';
                ctx.font = `${Math.max(14, w * 0.016)}px Inter, sans-serif`;
                ctx.fillText(state.drill.name + ' — ' + state.drill.subtitle, w / 2, h * 0.14);

                const remaining = state.totalShots - state.shotsHit;
                ctx.fillStyle = remaining === 0 ? '#2ecc71' : 'rgba(255,255,255,0.4)';
                ctx.font = `${Math.max(13, w * 0.014)}px Inter, sans-serif`;
                ctx.fillText(`Shots: ${state.shotsHit} / ${state.totalShots}`, w / 2, h * 0.19);

                // Full test progress indicator
                const isFullTest = drillRef.current?.isFullTest;
                if (isFullTest) {
                    const idx = fullTestDrillIdxRef.current;
                    ctx.fillStyle = 'rgba(255,255,255,0.3)';
                    ctx.font = `${Math.max(11, w * 0.012)}px Inter, sans-serif`;
                    ctx.fillText(`Drill ${idx + 1} of 4`, w / 2, h * 0.24);
                }
            }
        }
    }, []);

    useGameLoop(loop, true);

    // ── Canvas click ──────────────────────────────────────────────────────────
    const handleCanvasClick = useCallback((e) => {
        const state = stateRef.current;
        if (state.phase !== 'playing') return;

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const sx = canvas.width / rect.width;
        const sy = canvas.height / rect.height;
        const mx = (e.clientX - rect.left) * sx;
        const my = (e.clientY - rect.top) * sy;

        const drill = state.drill;
        const now = performance.now();

        state.totalClicks++;
        if (state.firstClickTime === null) {
            state.firstClickTime = Math.max(0, (now - state.startTime) / 1000 - inputLatencyMs / 1000);
        }

        let hitTarget = null;
        let isHit = false;

        if (!drill.isClick6 && !drill.isTexasStar) {
            const nextIdx = state.targets.findIndex(t => !t.hit);
            if (nextIdx !== -1) {
                hitTarget = state.targets[nextIdx];
                isHit = Math.abs(mx - hitTarget.cx) <= hitTarget.halfW && Math.abs(my - hitTarget.cy) <= hitTarget.halfH;
            }
        } else {
            for (const t of state.targets) {
                if (!t.hit) {
                    if (t.zone === 'plate') {
                        if (Math.hypot(mx - t.cx, my - t.cy) <= t.halfW) {
                            hitTarget = t;
                            isHit = true;
                            break;
                        }
                    } else {
                        if (Math.abs(mx - t.cx) <= t.halfW && Math.abs(my - t.cy) <= t.halfH) {
                            hitTarget = t;
                            isHit = true;
                            break;
                        }
                    }
                }
            }
            if (!hitTarget) hitTarget = state.targets.find(t => !t.hit);
        }

        if (!hitTarget) return;
        
        // Record the click location relative to the active target
        const targetIdx = state.targets.indexOf(hitTarget);
        state.clickLocations.push({
            x: (mx - hitTarget.cx) / hitTarget.halfW,
            y: (my - hitTarget.cy) / hitTarget.halfH,
            zone: hitTarget.zone,
            hit: isHit,
            ...(hitTarget.zone === 'plate' && { plateIndex: targetIdx }),
        });

        if (isHit) {
            hitTarget.hit = true;
            hitTarget.hitX = mx;
            if (drill.isTexasStar) {
                 hitTarget.attached = false;
                 hitTarget.thetaDrop = state.theta;
            }
            hitTarget.hitY = my;
            hitTarget.hitTime = Math.max(0, (now - state.startTime) / 1000 - inputLatencyMs / 1000);
            state.shotsHit++;
            ping();

            if (state.shotsHit >= state.totalShots) {
                state.phase = 'done';
                state.endTime = now;
                state.running = false;

                const totalTime = (state.endTime - state.startTime) / 1000;
                const isFullTest = drillRef.current?.isFullTest;

                if (isFullTest) {
                    // Accumulate this drill's time
                    const idx = fullTestDrillIdxRef.current;
                    fullTestTimesRef.current[idx] = totalTime;
                    const nextDrillIdx = idx + 1;

                    if (nextDrillIdx < DRILLS.length) {
                        // More drills — advance and standby
                        fullTestDrillIdxRef.current = nextDrillIdx;
                        activeDrillRef.current = DRILLS[nextDrillIdx];
                        // Small delay then standby
                        setTimeout(() => {
                            const nextDrill = activeDrillRef.current;
                            const canvas = canvasRef.current;
                            if (canvas && nextDrill) {
                                stateRef.current.drill = nextDrill;
                                stateRef.current.targets = buildTargets(nextDrill, canvas);
                                stateRef.current.phase = 'waiting';
                            } else {
                                stateRef.current.phase = 'idle';
                            }
                            setGameState('standby');
                        }, 800);
                    } else {
                        // All done — evaluate
                        const times = [...fullTestTimesRef.current];
                        const overallBelt = evaluateFullBelt(times, DRILLS, effectivePar);
                        const perDrill = DRILLS.map((d, i) => ({
                            drill: d,
                            time: times[i],
                            belt: evaluateBelt(d, times[i], effectivePar(d)),
                        }));
                        setFullTestResults({ perDrill, overallBelt });
                        saveEarnedBelt(overallBelt);
                        setResults(prev => [{
                            drill: 'full',
                            drillName: 'MSP Full Test',
                            time: Math.max(...times),
                            belt: overallBelt,
                        }, ...prev.slice(0, 19)]);
                        saveGameResult('modern-samurai', {
                            drill: 'full',
                            drillName: 'MSP Full Test',
                            time: Math.max(...times),
                            belt: overallBelt,
                        });
                        setHistory(getGameHistory('modern-samurai'));
                        setTimeout(() => {
                            stateRef.current.phase = 'idle';
                            setGameState('fullTestResult');
                        }, 800);
                    }
                } else {
                    // Regular single drill
                    const belt = evaluateBelt(drill, totalTime, effectivePar(drill));
                    saveEarnedBelt(belt);
                    const firstShot = state.totalShots === 1 ? state.firstClickTime : (state.targets[0]?.hitTime || 0);
                    const avgSplit = state.totalShots > 1 ? (totalTime - firstShot) / (state.totalShots - 1) : 0;

                    setResults(prev => [{
                        drill: drill.id,
                        drillName: drill.name,
                        time: totalTime,
                        belt,
                        parTimes: drill.parTimes,
                        firstShot,
                        avgSplit,
                        totalClicks: state.totalClicks,
                    }, ...prev.slice(0, 19)]);

                    saveGameResult('modern-samurai', {
                        drill: drill.id,
                        drillName: drill.name,
                        time: totalTime,
                        belt,
                        shots: state.shotsHit,
                        firstShot,
                        avgSplit,
                        totalClicks: state.totalClicks,
                        clickLocations: state.clickLocations,
                    });
                    setGameState('result');
                    setHistory(getGameHistory('modern-samurai'));
                }
            }
        }
    }, [ping, effectivePar]);

    // ── Draw Targets ──────────────────────────────────────────────────────────
    function drawTargets(ctx, targets, w, h, active, phase, state) {
        if (!targets || targets.length === 0) return;

        const bodyTargets = targets.filter(t => t.zone === 'body');
        const headTargets = targets.filter(t => t.zone === 'head');
        const plateTargets = targets.filter(t => t.zone === 'plate');
        const alpha = phase === 'waiting' ? 0.3 : 1.0;

        if (state && state.drill && state.drill.isTexasStar) {
            ctx.save();
            ctx.translate(w/2, h/2);
            ctx.rotate(state.theta);
            ctx.strokeStyle = "#777";
            ctx.lineWidth = 4;
            targets.forEach(p => {
                ctx.beginPath();
                ctx.moveTo(0, 0);
                ctx.lineTo(Math.cos(p.phi) * p.armPx, Math.sin(p.phi) * p.armPx);
                ctx.stroke();
            });
            ctx.beginPath();
            ctx.arc(0, 0, 20, 0, Math.PI * 2);
            ctx.fillStyle = "#aaa";
            ctx.fill();
            ctx.restore();
        }

        // A-zone (body) — 5.9" × 11"
        bodyTargets.forEach(t => {
            const { cx, cy, halfW, halfH } = t;
            ctx.globalAlpha = alpha;
            ctx.fillStyle = phase === 'done' ? '#1a1a1a' : GAME_THEME.TARGET_FILL;
            ctx.strokeStyle = GAME_THEME.TARGET_STROKE;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.rect(cx - halfW, cy - halfH, halfW * 2, halfH * 2);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = 'rgba(255,255,255,0.22)';
            ctx.font = `bold ${Math.max(10, halfW * 0.28)}px Inter, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText('A-ZONE', cx, cy + halfH - Math.max(6, halfH * 0.1));
        });

        // Head box — 4" × 6"
        headTargets.forEach(t => {
            const { cx, cy, halfW, halfH } = t;
            ctx.globalAlpha = alpha;
            ctx.fillStyle = phase === 'done' ? '#1a1a1a' : GAME_THEME.TARGET_FILL;
            ctx.strokeStyle = GAME_THEME.TARGET_STROKE;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.rect(cx - halfW, cy - halfH, halfW * 2, halfH * 2);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = 'rgba(255,255,255,0.22)';
            ctx.font = `bold ${Math.max(8, halfW * 0.35)}px Inter, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText('HEAD', cx, cy + halfH * 0.35);
        });

        // Steel Plates
        plateTargets.forEach(t => {
            const { cx, cy, halfW } = t;
            ctx.globalAlpha = alpha;
            ctx.fillStyle = phase === 'done' ? '#1a1a1a' : GAME_THEME.TARGET_FILL;
            ctx.strokeStyle = GAME_THEME.TARGET_STROKE;
            ctx.lineWidth = GAME_THEME.TARGET_STROKE_WIDTH || 2;
            ctx.beginPath();
            ctx.arc(cx, cy, halfW, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
        });

        // Hit markers
        ctx.globalAlpha = 1.0;
        targets.forEach((t, i) => {
            if (t.hit && t.hitX !== null) {
                ctx.fillStyle = '#e74c3c';
                ctx.strokeStyle = '#fff';
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                ctx.arc(t.hitX, t.hitY, Math.max(5, Math.min(10, t.halfW * 0.25)), 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();
                ctx.fillStyle = '#fff';
                ctx.font = `${Math.max(10, t.halfW * 0.22)}px Inter, monospace`;
                ctx.textAlign = 'center';
                ctx.fillText(t.hitTime.toFixed(3) + 's', t.hitX, t.hitY - Math.max(8, t.halfW * 0.22));
            } else if (!t.hit) {
                ctx.fillStyle = 'rgba(255,255,100,0.7)';
                ctx.font = `bold ${Math.max(14, t.halfW * 0.4)}px Inter, sans-serif`;
                ctx.textAlign = 'center';
                ctx.fillText(i + 1, t.cx, t.cy + Math.max(5, t.halfH * 0.1));
            }
        });
        ctx.globalAlpha = 1.0;
    }

    // ── Sidebar ───────────────────────────────────────────────────────────────
    const sidebarContent = (
        <>
            <button
                className={gameLayoutStyles.drillButton}
                onClick={() => setGameState('calibrating')}
                style={styles.sidebarStyles.calibrateBtn(gameState === 'calibrating')}
            >
                📐 Calibrate Screen Setup
                <div style={styles.sidebarStyles.calibrateInfo}>
                    Screen: {screenWidthIn.toFixed(1)}&quot; &nbsp;|&nbsp; Dist: {(viewDistIn / 12).toFixed(1)} ft
                </div>
            </button>

            {/* Laser latency offset */}
            <div style={styles.sidebarStyles.latencyBox(inputLatencyMs > 0)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#666' }}>⚡ Laser offset</span>
                    <span style={styles.sidebarStyles.latencyValue(inputLatencyMs > 0)}>
                        {inputLatencyMs > 0 ? `−${inputLatencyMs}ms` : 'not set'}
                    </span>
                </div>
                {inputLatencyMs === 0 && (
                    <div style={styles.sidebarStyles.latencyLink}>
                        Run <a href="/test-latency" style={styles.sidebarStyles.latencyLinkTag}>Latency Calibrator</a> to correct click times
                    </div>
                )}
            </div>

            <div style={{ marginBottom: '16px' }}>
                <div style={styles.sidebarStyles.sectionLabel}>Drill</div>
                
                {(() => {
                    const renderDrillButton = (d, i) => (
                        <button
                            key={d.id}
                            className={gameLayoutStyles.drillButton}
                            onClick={() => {
                                setSelectedDrillIndex(i);
                                const routes = [
                                    '/modern-samurai/3x2',
                                    '/modern-samurai/7yd',
                                    '/modern-samurai/bill',
                                    '/modern-samurai/25yd',
                                    '/modern-samurai/full',
                                    '/modern-samurai/3y-reload',
                                    '/modern-samurai/depth-transitions',
                                    '/modern-samurai/texas-star',
                                    '/modern-samurai/1-1-2-1-1',
                                    '/modern-samurai/click-6',
                                    '/modern-samurai/click-6-grid',
                                    '/modern-samurai/click-6-w'
                                ];
                                if (routes[i]) navigate(routes[i]);
                            }}
                            style={styles.sidebarStyles.drillButton(selectedDrillIndex === i, d.isFullTest)}
                        >
                            <div style={styles.sidebarStyles.drillName}>{d.name}</div>
                            <div style={styles.sidebarStyles.drillSubtitle}>
                                {d.isFullTest ? 'All standards • Earn your belt' : `${d.subtitle} — ${d.shots.length} shot${d.shots.length > 1 ? 's' : ''}`}
                            </div>
                        </button>
                    );

                    return (
                        <>
                            {ALL_DRILLS.slice(0, 5).map((d, i) => renderDrillButton(d, i))}
                            
                            {ALL_DRILLS.length > 5 && (
                                <div style={{ marginTop: '15px' }}>
                                    <button 
                                        onClick={() => setIsAdditionalExpanded(!isAdditionalExpanded)}
                                        className={gameLayoutStyles.drillButton}
                                        style={styles.sidebarStyles.accordionHeader(isAdditionalExpanded)}
                                    >
                                        <div style={styles.sidebarStyles.accordionHeaderText}>
                                            Additional Drills
                                        </div>
                                        <span style={styles.sidebarStyles.accordionArrow(isAdditionalExpanded)}>
                                            ▼
                                        </span>
                                    </button>
                                    <div 
                                        className={gameLayoutStyles.accordionContent}
                                        style={styles.sidebarStyles.accordionContent(isAdditionalExpanded)}
                                    >
                                        {ALL_DRILLS.slice(5).map((d, index) => renderDrillButton(d, 5 + index))}
                                    </div>
                                </div>
                            )}
                        </>
                    );
                })()}
            </div>

            {/* Recent results */}
            {results.length > 0 && (
                <div style={styles.sidebarStyles.resultsContainer}>
                    <div style={styles.sidebarStyles.sectionLabel}>Recent Runs</div>
                    {results.slice(0, 8).map((r, i) => (
                        <div key={i} style={styles.sidebarStyles.resultRow}>
                            <span style={{ color: '#888' }}>{r.drillName}</span>
                            <span style={styles.sidebarStyles.resultValue(r.belt)}>
                                {r.time.toFixed(3)}s{r.belt && r.belt !== 'miss' && ` (${r.belt})`}{r.belt === 'miss' && ' ✗'}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <div style={styles.sidebarStyles.footerButtons}>
                <button
                    onClick={() => setShowHistory(true)}
                    style={styles.sidebarStyles.footerBtn}
                >
                    📋 History
                </button>
                <button
                    onClick={() => setShowHeatMap(true)}
                    style={styles.sidebarStyles.footerBtn}
                >
                    🎯 Heat Map
                </button>
            </div>
        </>
    );

    // ── Overlays ──────────────────────────────────────────────────────────────
    const renderOverlay = () => {
        if (gameState === 'calibrating') {
            return (
                <CalibrationStep
                    initialScreenWidthIn={screenWidthIn}
                    initialViewDistIn={viewDistIn}
                    onApply={handleCalibrationApply}
                    onSkip={() => setGameState('menu')}
                />
            );
        }

        if (gameState === 'menu') {
            const d = ALL_DRILLS[selectedDrillIndex];
            return (
                <div style={styles.overlayStyles.overlay}>
                    <div style={styles.overlayStyles.overlayBox}>
                        <h2 style={{ margin: '0 0 6px', color: d.isFullTest ? '#2ecc71' : '#5dade2', fontSize: '22px' }}>{d.name}</h2>
                        <p style={{ color: '#aaa', margin: '0 0 16px', fontSize: '13px' }}>{d.description}</p>

                        {d.isFullTest ? (
                            // Full test: show all 4 drills and their par times for each belt
                            <div style={styles.overlayStyles.fullTestGrid}>
                                <div style={styles.overlayStyles.fullTestGridInner}>
                                    <div style={{ color: '#555', borderBottom: '1px solid #333', paddingBottom: '4px' }}>DRILL</div>
                                    {BELT_LEVELS.map(b => (
                                        <div key={b} style={styles.overlayStyles.beltHeader(b)}>
                                            {b}
                                        </div>
                                    ))}
                                    {DRILLS.map((drill) => (
                                        <React.Fragment key={drill.id}>
                                            <div style={{ color: '#ccc', whiteSpace: 'nowrap', fontWeight: 'bold' }}>{drill.name}</div>
                                            {BELT_LEVELS.map(b => (
                                                <div key={b} style={{ color: '#888', fontFamily: 'monospace', textAlign: 'center' }}>
                                                    {effectivePar(drill)[b].toFixed(1)}
                                                </div>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div style={styles.overlayStyles.singleDrillGrid}>
                                {BELT_LEVELS.map(belt => (
                                    <div key={belt} style={{ background: '#1e1e1e', border: `1px solid ${BELT_COLORS[belt]}44`, borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                                        <div style={{ color: BELT_COLORS[belt], fontWeight: 'bold', fontSize: '13px', textTransform: 'capitalize' }}>{belt}</div>
                                        <div style={{ color: '#ccc', fontFamily: 'monospace', fontSize: '15px' }}>≤ {effectivePar(d)[belt].toFixed(2)}s</div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <button
                            onClick={d.isFullTest ? beginFullTest : beginStandby}
                            style={styles.overlayStyles.startBtn(d.isFullTest)}
                        >
                            {d.isFullTest ? 'START FULL TEST' : 'START DRILL'}
                        </button>
                    </div>
                </div>
            );
        }

        if (gameState === 'standby') {
            const isFullTest = drillRef.current?.isFullTest;
            const label = isFullTest
                ? `Drill ${fullTestDrillIdxRef.current + 1} of 4 — ${DRILLS[fullTestDrillIdxRef.current]?.name}`
                : null;
            return (
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 20 }}>
                    <Countdown onFinish={startDrill} minDelay={isFullTest ? 5500 : 3500} maxDelay={isFullTest ? 7500 : 5500} />
                    {label && (
                        <div style={styles.overlayStyles.standbyLabel}>
                            {label}
                        </div>
                    )}
                </div>
            );
        }

        if (gameState === 'result') {
            const r = results[0];
            const belt = r?.belt;
            const passed = belt && belt !== 'miss';
            const drill = activeDrillRef.current;
            return (
                <div style={styles.overlayStyles.overlay}>
                    <div style={styles.overlayStyles.overlayBox}>
                        <div style={styles.overlayStyles.resultTime(passed, belt)}>
                            {r?.time.toFixed(3)}s
                        </div>
                        
                        {(r?.drill === 'bill' || r?.drill === '3x2' || r?.drill === '3y-reload') && (
                            <div style={styles.overlayStyles.resultStats}>
                                <div>FIRST SHOT: <span style={{ color: '#ccc', fontWeight: 'bold' }}>{r?.firstShot.toFixed(3)}s</span></div>
                                <div>{r?.drill === '3y-reload' ? 'RELOAD TIME' : 'AVG SPLIT'}: <span style={{ color: '#ccc', fontWeight: 'bold' }}>{r?.avgSplit.toFixed(3)}s</span></div>
                            </div>
                        )}

                        {passed
                            ? <div style={{ color: BELT_COLORS[belt], fontSize: '18px', fontWeight: 'bold', textTransform: 'capitalize', marginBottom: '6px' }}>{belt} Belt</div>
                            : <div style={{ color: '#e74c3c', fontSize: '16px', marginBottom: '6px' }}>Over par — keep training!</div>
                        }
                        <div style={{ color: '#888', fontSize: '12px', marginBottom: '16px' }}>{drill?.name} / {drill?.subtitle}</div>
                        <div style={styles.overlayStyles.progressBarContainer}>
                            {BELT_LEVELS.map(b => {
                                const par = effectivePar(drill)[b];
                                const achieved = r?.time <= par;
                                return (
                                    <div key={b} style={styles.overlayStyles.progressBarRow}>
                                        <div style={styles.overlayStyles.progressBarLabel(achieved, b)}>{b}</div>
                                        <div style={styles.overlayStyles.progressBarBackground}>
                                            <div style={styles.overlayStyles.progressBarFill(achieved, b, Math.min(100, (par / (r?.time || 1)) * 100))} />
                                        </div>
                                        <div style={{ color: '#555', fontSize: '11px', fontFamily: 'monospace', width: '44px' }}>≤{par.toFixed(2)}</div>
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={beginStandby} style={styles.overlayStyles.startBtn(false)}>AGAIN</button>
                        </div>
                    </div>
                </div>
            );
        }

        if (gameState === 'fullTestResult' && fullTestResults) {
            const { perDrill, overallBelt } = fullTestResults;
            const earned = overallBelt !== 'miss';
            return (
                <div style={styles.overlayStyles.overlay}>
                    <div style={{ ...styles.overlayStyles.overlayBox, maxWidth: '420px' }}>
                        {/* Overall verdict */}
                        <div style={{ fontSize: '32px', fontWeight: 'bold', fontFamily: 'monospace', color: earned ? BELT_COLORS[overallBelt] : '#e74c3c', marginBottom: '4px' }}>
                            {earned ? `${overallBelt.toUpperCase()} BELT` : 'NO BELT'}
                        </div>
                        <div style={{ color: '#888', fontSize: '12px', marginBottom: '16px' }}>MSP Full Test</div>

                        {/* Per-drill breakdown */}
                        <div style={{ width: '100%', marginBottom: '16px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto repeat(4, auto)', gap: '4px 8px', fontSize: '11px', marginBottom: '6px', color: '#555' }}>
                                <div>Drill</div>
                                <div style={{ textAlign: 'right', fontFamily: 'monospace' }}>Time</div>
                                {BELT_LEVELS.map(b => <div key={b} style={{ color: BELT_COLORS[b], textAlign: 'center', textTransform: 'capitalize' }}>{b[0].toUpperCase()}</div>)}
                            </div>
                            {perDrill.map(({ drill, time, belt: drillBelt }) => {
                                const pars = effectivePar(drill);
                                return (
                                    <div key={drill.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto repeat(4, auto)', gap: '4px 8px', fontSize: '11px', marginBottom: '4px', alignItems: 'center' }}>
                                        <div style={{ color: '#ccc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{drill.name}</div>
                                        <div style={{ fontFamily: 'monospace', textAlign: 'right', color: drillBelt === 'miss' ? '#e74c3c' : BELT_COLORS[drillBelt] }}>
                                            {time?.toFixed(3)}s
                                        </div>
                                        {BELT_LEVELS.map(b => (
                                            <div key={b} style={{ textAlign: 'center', fontSize: '13px' }}>
                                                {time <= pars[b] ? <span style={{ color: BELT_COLORS[b] }}>✓</span> : <span style={{ color: '#444' }}>✗</span>}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>

                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={beginFullTest} style={styles.overlayStyles.startBtn(true)}>RUN AGAIN</button>
                            <button onClick={() => setGameState('menu')} style={{ ...styles.overlayStyles.startBtn(false), background: '#222', border: '1px solid #444' }}>MENU</button>
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    };

    return (
        <GameLayout
            sidebarContent={sidebarContent}
            canvasRef={canvasRef}
            onCanvasClick={handleCanvasClick}
            gameRunning={gameState === 'playing'}
            infoButton={
                <button
                    onClick={() => setShowHowToPlay(true)}
                    title="How to Play"
                    style={styles.overlayStyles.infoBtn}
                >
                    ℹ
                </button>
            }
        >
            {renderOverlay()}
            {/* Earned belt badge — bottom-left */}
            {earnedBelt && (
                <div style={styles.badgeStyles.container(earnedBelt)}>
                    <div style={styles.badgeStyles.dot(earnedBelt)} />
                    <span style={styles.badgeStyles.text(earnedBelt)}>
                        {earnedBelt} Belt
                    </span>
                </div>
            )}

            <HistoryModal
                isOpen={showHistory}
                onClose={() => setShowHistory(false)}
                history={history}
                gameName="Modern Samurai"
                filterKey="drillName"
                filterValue={ALL_DRILLS[selectedDrillIndex].name}
                onDeletePoint={handleDeleteDataPoint}
            />

            <HeatMapModal
                isOpen={showHeatMap}
                onClose={() => setShowHeatMap(false)}
                history={history}
                gameName="Modern Samurai"
                filterKey="drillName"
                filterValue={ALL_DRILLS[selectedDrillIndex].name}
                hasHeadTarget={ALL_DRILLS[selectedDrillIndex].isFullTest || ALL_DRILLS[selectedDrillIndex].shots?.some(s => s.zone === 'head')}
                plateMode={
                    ALL_DRILLS[selectedDrillIndex].isTexasStar ? 'texas-star' :
                    ALL_DRILLS[selectedDrillIndex].isClick6 ? ALL_DRILLS[selectedDrillIndex].click6Mode :
                    null
                }
            />

            {/* ── How to Play Modal ─────────────────────────────────────────── */}
            {showHowToPlay && (
                <div
                    onClick={() => setShowHowToPlay(false)}
                    style={styles.modalStyles.overlay}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={styles.modalStyles.content}
                    >
                        <div style={styles.modalStyles.header}>
                            <h2 style={styles.modalStyles.title}>
                                ℹ How to Play — MSP Trainer
                            </h2>
                            <button
                                onClick={() => setShowHowToPlay(false)}
                                style={styles.modalStyles.closeBtn}
                            >✕</button>
                        </div>

                        <section style={styles.modalStyles.section}>
                            <h3 style={styles.modalStyles.sectionTitle}>🎯 Concealed Carry Standard</h3>
                            <p style={styles.modalStyles.text}>
                                All drills in the Modern Samurai Project are designed around a <strong style={{ color: '#e0e6f0' }}>concealed carry appendix draw</strong>. Par times assume you start with your hands at a neutral position (not pre-staged), cover garment in place, and draw from concealment before the first shot. Timing begins the moment you click Start and the beep fires.
                            </p>
                        </section>

                        <section style={styles.modalStyles.section}>
                            <h3 style={styles.modalStyles.sectionTitle}>⏱️ Par Times & Belt Levels</h3>
                            <p style={styles.modalStyles.text}>
                                Belt levels follow the MSP curriculum and are calibrated for a <strong style={{ color: '#e0e6f0' }}>carry pistol from concealment</strong> — not an open-carry competition rig. Each belt milestone represents a meaningful real-world standard:
                            </p>
                            <ul style={styles.modalStyles.list}>
                                <li><span style={{ color: '#f1c40f', fontWeight: 'bold' }}>Yellow</span> — Foundational; safe and deliberate</li>
                                <li><span style={{ color: '#27ae60', fontWeight: 'bold' }}>Green</span> — Functional; capable under stress</li>
                                <li><span style={{ color: '#2980b9', fontWeight: 'bold' }}>Blue</span> — Proficient; consistent performance</li>
                                <li><span style={{ color: '#8e44ad', fontWeight: 'bold' }}>Purple</span> — Advanced; competitive-grade speed</li>
                                <li><span style={{ color: '#a0522d', fontWeight: 'bold' }}>Brown</span> — Expert; exceptional carry performance</li>
                                <li><span style={{ color: '#222', fontWeight: 'bold', background: '#e0e6f0', padding: '1px 6px', borderRadius: '3px' }}>Black</span> — Elite; instructor / top-tier competitor level</li>
                            </ul>
                        </section>

                        <section style={styles.modalStyles.section}>
                            <h3 style={styles.modalStyles.sectionTitle}>🖱️ How Clicking Works</h3>
                            <p style={styles.modalStyles.text}>
                                Each click on the target represents one trigger press. The timer starts on the beep and stops when the last required hit is registered. <strong style={{ color: '#e0e6f0' }}>Only clicks inside the scoring zone count</strong> — misses do not stop the clock but are recorded. Use the optional <strong style={{ color: '#e0e6f0' }}>Latency Calibrator</strong> to subtract your hardware click delay from all results.
                            </p>
                        </section>

                        <section>
                            <h3 style={styles.modalStyles.sectionTitle}>📋 Drill Overview</h3>
                            <ul style={styles.modalStyles.list}>
                                <li><strong style={{ color: '#e0e6f0' }}>3×2 Drill</strong> — Draw and fire 2 shots on a 3-yard A-zone</li>
                                <li><strong style={{ color: '#e0e6f0' }}>7-Yard</strong> — One shot, center mass, 7 yards</li>
                                <li><strong style={{ color: '#e0e6f0' }}>Bill Drill</strong> — 6 shots, 7 yards, A-zone only</li>
                                <li><strong style={{ color: '#e0e6f0' }}>25-Yard</strong> — One precision shot, 25 yards</li>
                                <li><strong style={{ color: '#e0e6f0' }}>Full MSP Test</strong> — All four standards back-to-back; your worst time determines your belt</li>
                                <li><strong style={{ color: '#e0e6f0' }}>3-Yard Reload</strong> — Draw, fire, reload, fire; reload time is the key metric</li>
                                <li><strong style={{ color: '#e0e6f0' }}>Depth Transitions</strong> — One shot each on 3, 7, 15, and 25-yard targets</li>
                                <li><strong style={{ color: '#e0e6f0' }}>Texas Star</strong> — 5 moving steel plates; the star rotates as plates fall</li>
                                <li><strong style={{ color: '#e0e6f0' }}>1-1-2-1-1 Transitions</strong> — Three 7-yard targets in L→M→R→R→M→L order</li>
                                <li><strong style={{ color: '#e0e6f0' }}>Click 6</strong> — 6 steel plates in various layouts (Random, Grid, W-Pattern)</li>
                            </ul>
                        </section>
                    </div>
                </div>
            )}
        </GameLayout>
    );
};

SamuraiGame.propTypes = {
    initialDrillIndex: PropTypes.number
};

export default SamuraiGame;
